/* ==========================================================================
   Safe localStorage access

   `localStorage` is not always available: it THROWS on access (not merely
   returns null) in locked-down browser states — Safari Lockdown/Private on
   older iOS, storage disabled by enterprise policy, or storage blocked in a
   partitioned/embedded context. An unguarded call in the boot path (before
   React mounts) would propagate and leave the user staring at a blank page.

   These wrappers never throw: a failed read returns `null` (indistinguishable
   from "not stored"), and a failed write silently no-ops. Callers therefore
   degrade to "no stored session → show login" instead of crashing.
   ========================================================================== */

/** Read a key, or `null` if it is absent OR `localStorage` is inaccessible. */
export function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

/**
 * Write a key. Returns `true` if it was persisted, `false` if `localStorage`
 * was inaccessible or full. Callers that move data between keys should check
 * this before deleting the source, so a silently-failed write can't lose data.
 */
export function safeSetItem(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value)
    return true
  } catch {
    // Storage unavailable/full — nothing we can persist; degrade silently.
    return false
  }
}

/** Remove a key; silently no-op if `localStorage` is inaccessible. */
export function safeRemoveItem(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch {
    // Storage unavailable — nothing to remove; degrade silently.
  }
}
